import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';

interface UseResizableDrawerOptions {
  storageKey: string;
  defaultWidth: number;
  minWidth?: number;
  viewportMargin?: number;
}

interface ResizeHandleProps {
  role: 'separator';
  'aria-orientation': 'vertical';
  'aria-label': string;
  tabIndex: number;
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}

const getSidebarWidth = (): number => {
  const sidebar = document.querySelector<HTMLElement>('.sky-sidebar');
  if (sidebar) return sidebar.getBoundingClientRect().width;

  const value = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w');
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getMaxWidth = (viewportMargin: number): number => (
  Math.max(320, window.innerWidth - getSidebarWidth() - viewportMargin)
);

const clamp = (value: number, min: number, max: number): number => (
  Math.min(Math.max(value, min), max)
);

export const useResizableDrawer = ({
  storageKey,
  defaultWidth,
  minWidth = 420,
  viewportMargin = 24,
}: UseResizableDrawerOptions) => {
  const [width, setWidth] = useState(() => {
    if (typeof window === 'undefined') return defaultWidth;

    const saved = Number.parseInt(window.localStorage.getItem(storageKey) || '', 10);
    const maxWidth = getMaxWidth(viewportMargin);
    return clamp(Number.isFinite(saved) ? saved : defaultWidth, minWidth, maxWidth);
  });
  const [isResizing, setIsResizing] = useState(false);

  const commitWidth = useCallback((nextWidth: number) => {
    const maxWidth = getMaxWidth(viewportMargin);
    const clampedWidth = clamp(Math.round(nextWidth), minWidth, maxWidth);
    setWidth(clampedWidth);
    window.localStorage.setItem(storageKey, String(clampedWidth));
  }, [minWidth, storageKey, viewportMargin]);

  useEffect(() => {
    const onResize = () => {
      setWidth((current) => clamp(current, minWidth, getMaxWidth(viewportMargin)));
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minWidth, viewportMargin]);

  useEffect(() => {
    if (!isResizing) return;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    event.preventDefault();
    const pointerId = event.pointerId;
    event.currentTarget.setPointerCapture(pointerId);
    setIsResizing(true);

    const onPointerMove = (moveEvent: PointerEvent) => {
      commitWidth(window.innerWidth - moveEvent.clientX);
    };

    const onPointerUp = () => {
      setIsResizing(false);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  }, [commitWidth]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const delta = event.shiftKey ? 80 : 24;
    commitWidth(width + (event.key === 'ArrowLeft' ? delta : -delta));
  }, [commitWidth, width]);

  const handleProps: ResizeHandleProps = useMemo(() => ({
    role: 'separator',
    'aria-orientation': 'vertical',
    'aria-label': 'Resize drawer',
    tabIndex: 0,
    onPointerDown: handlePointerDown,
    onKeyDown: handleKeyDown,
  }), [handleKeyDown, handlePointerDown]);

  return {
    width,
    isResizing,
    handleProps,
  };
};
